import type { QueryClient } from '@tanstack/react-query';
import type { PlatformMessage } from '@liks/backend-svc-api/esm/models/index';
import { chatbotQueryKeys } from '../chatbots/chatbotQueryKeys';
import { chatQueryKeys } from '../chat/chatQueryKeys';
import {
  mergePlatformChatMessagesIntoCache,
  patchPlatformChatSessionPreview,
} from '../chat/platformChatMessagesCache';
import { knowledgeQueryKeys } from '../knowledge/knowledgeQueryKeys';
import { toast } from '../../toast/toast';
import { LiveEventKind, type LiveEvent } from '../../types/liveEvent';

export type ApplyLiveEventContext = {
  queryClient: QueryClient;
  pathname: string;
  translate: (key: string, values?: Record<string, string>) => string;
};

function isKnowledgeListPath(pathname: string): boolean {
  return pathname === '/knowledge';
}

function isChatbotsListPath(pathname: string): boolean {
  return pathname === '/chatbots';
}

function isChatbotPath(pathname: string, chatbotId: string): boolean {
  return (
    pathname === `/chatbots/${chatbotId}` ||
    pathname.startsWith(`/chatbots/${chatbotId}/`)
  );
}

function isPlatformChatPath(pathname: string, sessionId: string): boolean {
  return pathname === `/platform-chat/${sessionId}`;
}

function hasMessageId(
  message: PlatformMessage | undefined,
): message is PlatformMessage & { id: string } {
  return typeof message?.id === 'string' && message.id.length > 0;
}

function resourceName(
  value: string | null | undefined,
  fallback: string,
): string {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? fallback : trimmed;
}

function applyKnowledgeBaseAccessGranted(
  event: LiveEvent,
  context: ApplyLiveEventContext,
): void {
  const name = resourceName(
    event.knowledgeBaseName,
    context.translate('liveEvents.untitledKnowledgeDatabase'),
  );
  toast.info({
    title: context.translate('liveEvents.knowledgeAccessGrantedTitle'),
    description: context.translate(
      'liveEvents.knowledgeAccessGrantedDescription',
      { name },
    ),
  });

  const shouldRefetch =
    isKnowledgeListPath(context.pathname) ||
    isChatbotsListPath(context.pathname);
  void context.queryClient.invalidateQueries({
    queryKey: knowledgeQueryKeys.databases(),
    refetchType: shouldRefetch ? 'active' : 'none',
  });
}

function applyChatbotMembershipGranted(
  event: LiveEvent,
  context: ApplyLiveEventContext,
): void {
  const name = resourceName(
    event.chatbotName,
    context.translate('liveEvents.untitledChatbot'),
  );
  toast.info({
    title: context.translate('liveEvents.chatbotMembershipGrantedTitle'),
    description: context.translate(
      'liveEvents.chatbotMembershipGrantedDescription',
      { name },
    ),
  });

  void context.queryClient.invalidateQueries({
    queryKey: chatbotQueryKeys.list(),
    refetchType: isChatbotsListPath(context.pathname) ? 'active' : 'none',
  });

  const chatbotId = event.chatbotId;
  if (
    typeof chatbotId === 'string' &&
    chatbotId.length > 0 &&
    isChatbotPath(context.pathname, chatbotId)
  ) {
    void context.queryClient.invalidateQueries({
      queryKey: chatbotQueryKeys.chatbot(chatbotId),
    });
  }
}

function applyPlatformChatParticipantAdded(
  event: LiveEvent,
  context: ApplyLiveEventContext,
): void {
  const sessionTitle = event.sessionTitle?.trim() ?? '';
  toast.info({
    title: context.translate('liveEvents.chatParticipantAddedTitle'),
    description:
      sessionTitle === ''
        ? context.translate(
            'liveEvents.chatParticipantAddedDescriptionUntitled',
          )
        : context.translate('liveEvents.chatParticipantAddedDescription', {
            name: sessionTitle,
          }),
  });

  void context.queryClient.invalidateQueries({
    queryKey: chatQueryKeys.sessions(),
  });
}

function applyPlatformChatMessageReceived(
  event: LiveEvent,
  context: ApplyLiveEventContext,
): void {
  const sessionId = event.sessionId;
  if (typeof sessionId !== 'string' || sessionId.length === 0) {
    return;
  }

  const messagesQueryKey = chatQueryKeys.messages(sessionId);
  const onSessionPath = isPlatformChatPath(context.pathname, sessionId);

  if (hasMessageId(event.message)) {
    if (onSessionPath) {
      mergePlatformChatMessagesIntoCache(context.queryClient, sessionId, [
        event.message,
      ]);
    } else {
      void context.queryClient.invalidateQueries({
        queryKey: messagesQueryKey,
        refetchType: 'none',
      });
    }

    const foundInSidebar = patchPlatformChatSessionPreview(
      context.queryClient,
      sessionId,
      event.message,
    );

    if (!foundInSidebar) {
      void context.queryClient.invalidateQueries({
        queryKey: chatQueryKeys.sessions(),
        refetchType: 'active',
      });
    }

    return;
  }

  if (onSessionPath) {
    void context.queryClient.invalidateQueries({
      queryKey: messagesQueryKey,
    });
  } else {
    void context.queryClient.invalidateQueries({
      queryKey: messagesQueryKey,
      refetchType: 'none',
    });
  }

  void context.queryClient.invalidateQueries({
    queryKey: chatQueryKeys.sessions(),
    refetchType: 'active',
  });
}

export function applyLiveEvent(
  event: LiveEvent,
  context: ApplyLiveEventContext,
): void {
  switch (event.kind) {
    case LiveEventKind.KnowledgeBaseAccessGranted:
      applyKnowledgeBaseAccessGranted(event, context);
      return;
    case LiveEventKind.ChatbotMembershipGranted:
      applyChatbotMembershipGranted(event, context);
      return;
    case LiveEventKind.PlatformChatParticipantAdded:
      applyPlatformChatParticipantAdded(event, context);
      return;
    case LiveEventKind.PlatformChatMessageReceived:
      applyPlatformChatMessageReceived(event, context);
      return;
    default:
      return;
  }
}

export function reloadVisibleLiveEventQueries(
  queryClient: QueryClient,
  pathname: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: chatQueryKeys.messagesRoot(),
    refetchType: 'active',
  });
  void queryClient.invalidateQueries({
    queryKey: chatbotQueryKeys.list(),
    refetchType: isChatbotsListPath(pathname) ? 'active' : 'none',
  });
  void queryClient.invalidateQueries({
    queryKey: knowledgeQueryKeys.databases(),
    refetchType:
      isKnowledgeListPath(pathname) || isChatbotsListPath(pathname)
        ? 'active'
        : 'none',
  });
  void queryClient.invalidateQueries({
    queryKey: chatQueryKeys.sessions(),
  });
}
